import { Box, Button, Card, CardContent } from '@mui/material';
import PageWithTitleLayout from 'components/layouts/page-with-title/page-with-title-layout';
import HelmetTabComponent from 'components/helmet-tab/helmet-tab';
import { StepsLayoutType } from './register.types';

const neutralBackButtonSx = (theme: any) => ({
    minHeight: 40,
    height: 40,
    px: 2,
    color: theme.palette.text.primary,
    borderColor:
        theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,.22)'
            : theme.palette.divider,
    backgroundColor: theme.palette.background.paper,
    '& .MuiButton-startIcon': { color: 'inherit' },
    '&:hover': {
        color: theme.palette.text.primary,
        borderColor:
            theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,.38)'
                : theme.palette.grey[400],
        backgroundColor:
            theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,.04)'
                : theme.palette.action.hover,
    },
});

const ticketActionButtonSx = {
    width: { xs: 96, sm: 104 },
    minWidth: { xs: 96, sm: 104 },
    maxWidth: { xs: 96, sm: 104 },
    minHeight: 40,
    height: 40,
    px: { xs: 0.75, sm: 1 },
    py: 0,
    fontSize: { xs: 10, sm: 10.75 },
    lineHeight: 1,
    whiteSpace: 'nowrap',
    flex: { xs: '0 0 96px', sm: '0 0 104px' },
    boxSizing: 'border-box',
    justifyContent: 'center',
    alignItems: 'center',
    verticalAlign: 'middle',
    alignSelf: 'center',
};

export const StepsLayout: React.FC<StepsLayoutType> = ({
    step,
    onPrev,
    children,
}) => {
    const { label, disable_back, name } = step;
    const isTicket = name === 'ticket';
    const isProfile = name === 'profile';
    const isBank = label === 'اطلاعات بانکی';
    const isDocuments = label === 'ارسال مدارک';

    const actions = (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                flexWrap:
                    isBank || isDocuments || isTicket
                        ? { xs: 'nowrap', sm: 'wrap' }
                        : 'wrap',
                gap:
                    isBank || isDocuments || isTicket
                        ? { xs: 0.5, sm: 1 }
                        : 1,
                '& .MuiButton-root': {
                    minHeight: isBank || isDocuments ? { xs: 36, sm: 40 } : 40,
                    height: isBank || isDocuments ? { xs: 36, sm: 40 } : 40,
                    ...(isBank || isDocuments
                        ? {
                              px: { xs: 1, sm: 2 },
                              fontSize: { xs: 10.5, sm: 13 },
                              whiteSpace: 'nowrap',
                          }
                        : {}),
                },
            }}>
            {isProfile && <Box id="register-profile-header-action" />}

            {isTicket ? (
                <>
                    <Button
                        variant="outlined"
                        size="medium"
                        onClick={() => onPrev && onPrev()}
                        sx={(theme) => ({
                            ...neutralBackButtonSx(theme),
                            ...ticketActionButtonSx,
                        })}>
                        بازگشت به ثبت‌نام
                    </Button>
                    <Box
                        id="register-ticket-header-actions"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            alignSelf: 'center',
                            height: 40,
                            gap: 1,
                            flexWrap: 'wrap',
                            '& .MuiButton-root': ticketActionButtonSx,
                            '& .MuiGrid-container': {
                                margin: '0 !important',
                                width: 'auto !important',
                                height: 40,
                                alignItems: 'center',
                            },
                            '& .MuiGrid-item': {
                                width: { xs: 96, sm: 104 },
                                minWidth: { xs: 96, sm: 104 },
                                maxWidth: { xs: 96, sm: 104 },
                                flex: { xs: '0 0 96px', sm: '0 0 104px' },
                                padding: '0 !important',
                                height: 40,
                                display: 'flex',
                                alignItems: 'center',
                            },
                        }}
                    />
                </>
            ) : (
                <>
                    {!disable_back && (
                        <Button
                            variant="outlined"
                            size="medium"
                            startIcon={<i className="fa-light fa-arrow-right" />}
                            onClick={() => onPrev && onPrev()}
                            sx={neutralBackButtonSx}>
                            بازگشت
                        </Button>
                    )}
                    {isBank && <Box id="register-bank-header-action" />}
                    {isDocuments && (
                        <Box id="register-documents-header-action" />
                    )}
                </>
            )}
        </Box>
    );

    if (isTicket) {
        return (
            <>
                <HelmetTabComponent title={label} />
                <Box
                sx={{
                    height: '100%',
                    minHeight: 0,
                    '& > .MuiBox-root': {
                        height: '100%',
                        minHeight: 0,
                    },
                    '& > .MuiBox-root > .MuiBox-root:last-of-type': {
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                    },
                }}>
                <PageWithTitleLayout
                    title={label}
                    slot={actions}
                    mobileInlineHeader>
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                        }}>
                        {children}
                    </Box>
                </PageWithTitleLayout>
                </Box>
            </>
        );
    }

    return (
        <>
            <HelmetTabComponent title={label} />
            <Box sx={{ minHeight: 0 }}>
            <PageWithTitleLayout
                title={label}
                slot={actions}
                mobileInlineHeader={isBank || isDocuments}>
                <Card variant="outlined" sx={{ minHeight: 0, overflow: 'visible' }}>
                    <CardContent
                        sx={{
                            width: '100%',
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            p: { xs: 1.5, sm: 2 },
                            '&:last-child': { pb: { xs: 1.5, sm: 2 } },
                        }}>
                        <Box sx={{ width: '100%', minHeight: 0 }}>{children}</Box>
                    </CardContent>
                </Card>
            </PageWithTitleLayout>
            </Box>
        </>
    );
};
